import {
  SMS_MESSAGES_FETCH_REQUESTED,
  SMS_MESSAGES_FETCH_REQUEST_SUCCESS,
  SMS_MESSAGES_FETCH_REQUEST_ERROR,
  SMS_SET_FILTERS,
  SMS_FETCH_MORE_REQUESTED,
  SMS_FETCH_MORE_REQUEST_SUCCESS,
  SMS_FETCH_MORE_REQUEST_ERROR,
  SMS_UPDATE_SECTION,
  SMS_SET_MARKED_FOR_ACTION,
  SMS_SET_CURRENT,
  SMS_RESET_CURRENT,
  SMS_FETCH_REQUEST_SUCCESS,
  SMS_RESET_MARKED_FOR_ACTION,
  SMS_REMOVE_FROM_LIST,
  SMS_SET_HANDLED_BY_SUCCESS,
  SMS_REMOVE_HANDLED_BY_SUCCESS,
  SMS_DELETESTATUS_RESET,
  SMS_DELETE_ERROR,
  SMS_DELETE_REQUESTED,
  SMS_DELETE_SUCCESS,
  SMS_RESTORE_SUCCESS,
  SMS_SET_SINGLE_LIST_ACTION_ID,
  SMS_FETCH_SILENT_SUCCESS,
  SMS_SET_LABELS,
  SMS_REMOVE_LABEL,
  SMS_FETCH_NEW_SILENTLY_SUCCESS,
  SMS_UPDATE_DRAFT,
} from './actionTypes'
import { merge, mergeAndConcat } from 'shared/utils/mergeObjects'
import { SmsActionTypes } from './types'
import { ICommonListModel } from 'shared/models/Common'
import {
  ISmsListItem,
  ISmsModel,
} from 'Modules/Communication/Sms/shared/models/Sms'
import {
  IDatesQueryModel,
  InboxDeleteStatus,
  InboxSectionEnum,
  MessageState,
} from 'Modules/Communication/shared/models/Inbox'
import { produce } from 'immer'

const initialState = {
  data: null as ICommonListModel<ISmsListItem> | null,
  error: null,
  loading: false,
  loadingMore: false,
  updated: new Date(),
  filters: {
    searchText: '',
    queryStatus: InboxSectionEnum.Sent,
    labelId: '',
    take: 20,
    skip: 0,
    sortcolumn: 'SentDate',
    dates: {
      received: {
        from: undefined,
        to: undefined,
      },
      sent: {
        from: undefined,
        to: undefined,
      },
      deleted: {
        from: undefined,
        to: undefined,
      },
      processed: {
        from: undefined,
        to: undefined,
      },
    } as IDatesQueryModel,
    onlyUnread: false,
  },

  sms: {
    current: null,
    data: null as ISmsModel | null,
    error: null,
    loading: false,
    completed: false,
  },

  marked: [] as string[],
  singleListActionId: null as string | null,
  deleteStatus: null as InboxDeleteStatus | null,
}

const reducer = (state = initialState, action: SmsActionTypes) => {
  switch (action.type) {
    case SMS_MESSAGES_FETCH_REQUESTED:
      return merge(state, { data: null, error: null, loading: true })

    case SMS_MESSAGES_FETCH_REQUEST_SUCCESS:
      return merge(state, {
        loading: false,
        error: null,
        data: action.payload,
        updated: new Date(),
      })

    case SMS_MESSAGES_FETCH_REQUEST_ERROR:
      return merge(state, {
        loading: false,
        error: action.payload,
      })

    case SMS_FETCH_NEW_SILENTLY_SUCCESS: {
      const incomingSms = action.payload

      const dataWithType =
        state.data as unknown as ICommonListModel<ISmsListItem>

      // Only show new incoming sms out of dataWithType
      const newMessages = incomingSms.filter(
        (sms) => !dataWithType.result.find((item) => item.id === sms.id)
      )

      if (newMessages.length === 0) return state

      const newResult = [...newMessages, ...dataWithType.result]

      return merge(state, {
        data: {
          result: newResult,
          totalCount: dataWithType.totalCount + newMessages.length,
          totalPageCount: dataWithType.totalPageCount,
          resultCount: dataWithType.resultCount + newMessages.length,
          morePages: dataWithType.morePages,
          currentPage: dataWithType.currentPage,
        },
      })
    }

    case SMS_SET_FILTERS: {
      // Removed marked objects when swapping labels or changing searchTexts
      const shouldResetMarked = Object.keys(action.payload).some((item) =>
        ['labelId', 'queryStatus', 'searchText'].includes(item)
      )

      return merge(state, {
        marked: shouldResetMarked ? [] : state.marked,
        filters: { ...action.payload, skip: 0 },
      })
    }

    case SMS_FETCH_MORE_REQUESTED:
      return merge(state, {
        loadingMore: true,
        filters: {
          skip: state.filters.skip + 20,
        },
      })

    case SMS_FETCH_MORE_REQUEST_SUCCESS:
      return mergeAndConcat(state, {
        loadingMore: false,
        data: action.payload,
        updated: new Date(),
      })

    case SMS_FETCH_MORE_REQUEST_ERROR:
      return merge(state, {
        loadingMore: false,
        error: action.payload,
      })

    case SMS_UPDATE_SECTION: {
      const { section, sortcolumn } = action.payload

      return produce(state, (draft) => {
        draft.filters.queryStatus = section
        draft.filters.sortcolumn = sortcolumn
        draft.filters.skip = 0
        draft.filters.dates = {
          received: {
            from: undefined,
            to: undefined,
          },
          sent: {
            from: undefined,
            to: undefined,
          },
          deleted: {
            from: undefined,
            to: undefined,
          },
          processed: {
            from: undefined,
            to: undefined,
          },
        }
        draft.marked = []
      })
    }

    case SMS_SET_MARKED_FOR_ACTION: {
      const id = action.payload

      if (state.marked.includes(id)) {
        return merge(state, {
          marked: state.marked.filter((item) => item !== id),
        })
      }

      return mergeAndConcat(state, {
        sms: {
          current: null,
          data: null,
        },
        marked: [action.payload],
      })
    }

    case SMS_RESET_MARKED_FOR_ACTION: {
      return merge(state, {
        marked: [],
      })
    }

    case SMS_SET_CURRENT: {
      return merge(state, {
        sms: {
          current: action.payload.id,
          completed: action.payload.id !== null ? false : true,
          data: null,
          error: null,
          loading: true,
        },
      })
    }

    case SMS_RESET_CURRENT: {
      return merge(state, {
        sms: {
          current: null,
          completed: true,
          data: null,
          error: null,
          loading: false,
        },
      })
    }

    case SMS_FETCH_REQUEST_SUCCESS: {
      return merge(state, {
        sms: {
          completed: true,
          data: action.payload,
          error: null,
          loading: false,
        },
      })
    }

    case SMS_REMOVE_FROM_LIST: {
      return produce(state, (draft) => {
        draft.marked = []
        if (draft.data) {
          draft.data.result = draft.data.result.filter(
            (item) => !action.payload.some((labelId) => labelId === item.id)
          )
        }
      })
    }

    case SMS_REMOVE_HANDLED_BY_SUCCESS: {
      return produce(state, (draft) => {
        if (
          draft.data?.result.length &&
          draft.filters.queryStatus !== InboxSectionEnum.All
        ) {
          draft.data.result = draft.data.result.filter(
            (item) => item.id !== action.payload
          )
        }

        if (draft.data && draft.filters.queryStatus === InboxSectionEnum.All) {
          draft.data.result = draft.data.result.map((item) => {
            if (draft.sms.data && action.payload === item.id) {
              item.state = String(draft.sms.data.status) as MessageState
            }

            return item
          })
        }
      })
    }

    case SMS_SET_HANDLED_BY_SUCCESS: {
      const messageIds = action.payload.messageIds

      return produce(state, (draft) => {
        if (
          draft.filters.queryStatus !== InboxSectionEnum.Processed &&
          draft.filters.queryStatus !== InboxSectionEnum.All &&
          draft.data
        ) {
          draft.data.result = draft.data.result.filter(
            (item) => !messageIds.some((messageId) => messageId === item.id)
          )
          draft.marked = []
        }

        if (draft.data && draft.filters.queryStatus === InboxSectionEnum.All) {
          draft.data.result = draft.data.result.map((item) => {
            if (messageIds.includes(item.id)) {
              item.state = MessageState.Processed
            }

            return item
          })
        }
      })
    }

    case SMS_DELETE_REQUESTED: {
      return produce(state, (draft) => {
        draft.deleteStatus = InboxDeleteStatus.isDeleting
      })
    }
    case SMS_DELETE_SUCCESS: {
      return produce(state, (draft) => {
        draft.deleteStatus = InboxDeleteStatus.success
        draft.marked = []

        if (draft.data) {
          // Only one sms can be handled if we are in All (label section)
          if (draft.filters.queryStatus === InboxSectionEnum.All) {
            if (draft.sms.data?.state === MessageState.Deleted) {
              draft.data.result.filter(
                (item) =>
                  !action.payload.some((deleteId) => deleteId === item.id)
              )
            } else {
              draft.data.result = draft.data.result.map((item) => {
                if (action.payload.includes(item.id)) {
                  item.state = MessageState.Deleted
                }
                return item
              })
            }
          } else {
            draft.data.result = draft.data.result.filter(
              (item) =>
                !action.payload.some((deletedId) => deletedId === item.id)
            )
          }
        }
      })
    }
    case SMS_DELETE_ERROR: {
      return produce(state, (draft) => {
        draft.deleteStatus = InboxDeleteStatus.error
      })
    }
    case SMS_DELETESTATUS_RESET: {
      return produce(state, (draft) => {
        draft.deleteStatus = null
      })
    }

    case SMS_RESTORE_SUCCESS: {
      return produce(state, (draft) => {
        draft.deleteStatus = InboxDeleteStatus.success
        draft.marked = []
        if (draft.data) {
          if (draft.filters.queryStatus === InboxSectionEnum.All) {
            draft.data.result = draft.data.result.map((item) => {
              if (action.payload.includes(item.id) && draft.sms.data?.status) {
                if (draft.sms.data.processedDate) {
                  item.state = MessageState.Processed
                } else {
                  item.state = String(draft.sms.data.status) as MessageState
                }
              }
              return item
            })
          } else {
            draft.data.result = draft.data.result.filter(
              (item) =>
                !action.payload.some((deletedId) => deletedId === item.id)
            )
          }
        }

        if (draft.sms.data) draft.sms.data = null
      })
    }

    case SMS_SET_SINGLE_LIST_ACTION_ID: {
      return produce(state, (draft) => {
        draft.singleListActionId = action.payload
      })
    }

    case SMS_FETCH_SILENT_SUCCESS: {
      return produce(state, (draft) => {
        if (!action.payload?.onlyFetchLabels)
          draft.sms.data = action.payload.sms
        else if (draft.sms.data)
          draft.sms.data.labels = action.payload.sms?.labels || []
      })
    }

    case SMS_SET_LABELS: {
      return produce(state, (draft) => {
        // Add the labels to the current sms if it exists
        if (draft.sms.data) {
          action.payload.forEach((label) => {
            if (!draft.sms.data?.labels?.some((item) => item.id === label.id))
              draft.sms.data?.labels?.push(label)
          })
        }

        // If we have marked sms´s, add the new labels to them if they dont already have them
        const listIds = draft.sms.current ? [draft.sms.current] : draft.marked

        listIds?.forEach((id) => {
          const sms = draft.data?.result?.find((item) => item.id === id)

          if (sms) {
            action.payload.forEach((newLabel) => {
              if (!sms.labels?.some((item) => item.id === newLabel.id)) {
                sms.labels?.push(newLabel)
              }
            })
          }
        })
      })
    }

    case SMS_REMOVE_LABEL: {
      return produce(state, (draft) => {
        const currentSmsId = state.sms.current

        if (draft.filters.queryStatus === InboxSectionEnum.All) {
          draft.sms.current = null
          draft.sms.data = null

          // Remove current sms from list
          if (draft.data?.result.length) {
            draft.data.result = draft.data?.result.filter(
              (item) => item.id !== currentSmsId
            )
          }
        } else {
          if (draft.sms.data) {
            draft.sms.data.labels = draft.sms.data?.labels?.filter(
              (label) => label.id !== action.payload
            )
          }
        }

        const listItem = draft.data?.result?.find(
          (listItem) => listItem.id === currentSmsId
        )

        if (listItem)
          listItem.labels = listItem.labels.filter(
            (label) => label.id !== action.payload
          )
      })
    }

    case SMS_UPDATE_DRAFT: {
      const id = action.payload.id
      return produce(state, (draft) => {
        // Update draft sms in list and selected sms if exists
        const smsInList = draft.data?.result?.find((x) => x.id === id)

        if (smsInList) smsInList.content = action.payload.value
        if (draft.sms?.data?.id === id)
          draft.sms.data.content = action.payload.value
      })
    }

    default:
      return state
  }
}

export default reducer
